import type { Payment } from '@klopotun/contracts';
import {
  nextPeriodOutsidePause,
  parseCivilDate,
  toCivilDate,
  type CivilDate,
  type PauseWindow,
  type RecurrenceRule,
} from '@klopotun/domain';

const recurrenceRuleOf = (payment: Payment): RecurrenceRule => ({
  intervalUnit: payment.intervalUnit,
  intervalCount: payment.intervalCount,
  anchorDay: payment.anchorDay,
  anchorMonth: payment.anchorMonth,
  startDate: parseCivilDate(payment.startDate),
  endDate: payment.endDate === null ? null : parseCivilDate(payment.endDate),
});

const pauseWindowOf = (payment: Payment): PauseWindow | null => {
  if (payment.status !== 'paused') {
    return null;
  }

  return {
    pausedFrom: payment.pausedFrom === null ? null : parseCivilDate(payment.pausedFrom),
    resumeOn: payment.resumeOn === null ? null : parseCivilDate(payment.resumeOn),
  };
};

/**
 * The next due date the template alone produces, for the stretch the materialised periods do not
 * cover. They stop at the horizon - the end of the calendar year, at least three months out - so
 * a payment that repeats less often than that has no row ahead of it for most of the year: a
 * yearly domain renewal has none for nine months out of twelve. The screens take the date of a
 * materialised period when there is one and fall back to this forecast when there is not.
 *
 * `now` is an argument on purpose - the clock is never read here, exactly as in packages/domain.
 * The pause rule itself lives in packages/domain, where a test runner can reach it.
 */
export const nextDueDate = (payment: Payment, now: Date, timeZone: string): CivilDate | null => {
  if (payment.status === 'cancelled') {
    return null;
  }

  const today = toCivilDate(now, timeZone);
  const period = nextPeriodOutsidePause(recurrenceRuleOf(payment), pauseWindowOf(payment), today);

  return period?.dueDate ?? null;
};
